import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Choose which decks a card is in (Create and Edit share this). Each deck
 * is a real toggle button — keyboard-reachable, announced as pressed or
 * not — and with no decks yet it says where to make one instead of a dead
 * end.
 */
function DeckPicker({
  decks,
  loading = false,
  selected,
  onToggle,
  labelledBy,
}) {
  if (loading) {
    return (
      <div className="flex items-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 size-4 animate-spin" />
        Loading decks…
      </div>
    );
  }
  if (decks.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No decks yet.{" "}
        <Link to="/study/decks" className="underline underline-offset-2">
          Make one on the Decks page
        </Link>{" "}
        to review a topic on its own — you can add this card to it later.
      </p>
    );
  }
  return (
    <div
      className="flex flex-wrap gap-2"
      role="group"
      aria-labelledby={labelledBy}
    >
      {decks.map((deck) => {
        const on = selected.includes(deck.id);
        return (
          <button
            key={deck.id}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(deck.id)}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
              on
                ? "border-primary bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
            )}
          >
            {on && <Check className="size-3.5" aria-hidden="true" />}
            {deck.name}
          </button>
        );
      })}
    </div>
  );
}

DeckPicker.propTypes = {
  decks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  loading: PropTypes.bool,
  selected: PropTypes.arrayOf(PropTypes.number).isRequired,
  onToggle: PropTypes.func.isRequired,
  /** id of the visible "Decks" label, so the group is named. */
  labelledBy: PropTypes.string,
};

export default DeckPicker;
